import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SectionHeading } from '../components/shared/SectionHeading';
import { ServiceCard } from '../components/shared/ServiceCard';
import { useLanguage } from '../context/LanguageContext';
import * as Icons from 'lucide-react';
import { ArrowRight, ChevronRight, Shield, CheckCircle, Star, Lock, FileCheck } from 'lucide-react';

// Ad Platform Partners
const adPartners = [
  { name: 'META', logo: 'img/meta.png' },
  { name: 'GOOGLE ADS', logo: 'img/google.png' },
  { name: 'TIKTOK ADS', logo: 'img/tiktok.png' },
  { name: 'SNAPCHAT ADS', logo: 'img/snap.png' },
  { name: 'BIGO ADS', logo: 'img/bigo.png' },
  { name: 'TABOOLA', logo: 'img/tab.png' },
  { name: 'EVADAV', logo: 'img/evadav.png' },
  { name: 'MGID', logo: 'img/mg.webp' },
];

const verticalsList = [
  'Finance',
  'Insurance',
  'E-commerce',
  'Retail',
  'Mobility',
  'Sweepstakes',
  'FMCG',
];

export default function Home() {
  const { t } = useLanguage();

  const uspsData = [
    { key: 'directSource', icon: 'Target' },
    { key: 'gdprCompliant', icon: 'Shield' },
    { key: 'highDeliverability', icon: 'Send' },
    { key: 'realTimeValidation', icon: 'CheckCircle' },
    { key: 'customFunnels', icon: 'Settings' },
    { key: 'transparentReporting', icon: 'BarChart3' },
  ];

  const servicesData = [
    { key: 'coReg', icon: 'Users' },
    { key: 'coSpo', icon: 'Award' },
    { key: 'email', icon: 'Mail' },
    { key: 'sms', icon: 'Smartphone' },
    { key: 'leadAcquisition', icon: 'Gift' },
    { key: 'phone', icon: 'Phone' },
    { key: 'data', icon: 'Database' },
    { key: 'api', icon: 'Zap' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/20" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
        <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="max-w-2xl">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-900/20 border border-emerald-800 text-emerald-400 text-sm font-medium mb-8">
                <Star className="w-4 h-4 mr-2 fill-current" />
                {t.hero.badge}
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
                {t.hero.title}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500">
                  {t.hero.titleHighlight}
                </span>
              </h1>

              <p className="text-xl text-slate-400 leading-relaxed mb-10">{t.hero.description}</p>

              <div className="flex flex-wrap gap-4 mb-12">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl shadow-emerald-600/25 hover:shadow-emerald-600/40 transition-all duration-300 h-14 px-8 text-base">
                    {t.hero.getStarted}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-base border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
                    {t.hero.exploreServices}
                  </Button>
                </Link>
              </div>
            </div>
            {/* Stats Cards */}{' '}
            {/* <div className="group relative bg-slate-800/50 rounded-2xl p-6 shadow-xl shadow-slate-900/50 border border-slate-700/50 hover:border-emerald-800 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500 mb-2">
                    500+
                  </div>
                  <div className="text-sm text-slate-400">{t.stats.campaignsDelivered}</div>
                </div> */}
            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Верхний элемент — по центру, растягиваем на 2 колонки */}
                <div className="md:col-span-2 mx-auto w-full md:w-1/2">
                  <div className="group relative bg-slate-800/50 rounded-2xl p-6 shadow-xl shadow-slate-900/50 border border-slate-700/50 hover:border-emerald-800 transition-all duration-300">
                    <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500 mb-2">
                      24/7
                    </div>
                    <div className="text-sm text-slate-400">{t.stats.leadsGenerated}</div>
                  </div>
                </div>

                {/* Нижние два элемента — обычные */}
                <div className="group relative bg-slate-800/50 rounded-2xl p-6 shadow-xl shadow-slate-900/50 border border-slate-700/50 hover:border-emerald-800 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500 mb-2">
                    99.8%
                  </div>
                  <div className="text-sm text-slate-400">{t.stats.emailDeliverability}</div>
                </div>

                <div className="group relative bg-slate-800/50 rounded-2xl p-6 shadow-xl shadow-slate-900/50 border border-slate-700/50 hover:border-emerald-800 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500 mb-2">
                    100%
                  </div>
                  <div className="text-sm text-slate-400">{t.stats.gdprCompliant}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-sm text-slate-400 mb-8 uppercase tracking-wider">
            {t.partners.title}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-6 md:gap-x-12">
            {adPartners.map((partner, i) => (
              <div
                key={i}
                className="group flex items-center gap-2  text-slate-400 transition-all duration-300 cursor-default  hover:opacity-100">
                <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-xs font-bold bg-emerald-900/30 text-emerald-400 transition-all duration-300">
                  <img width="80%" src={partner.logo} />
                </div>
                <span className="text-sm md:text-base font-semibold tracking-wide">
                  {partner.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            badge={t.services.badge}
            title={t.services.title}
            subtitle={t.services.subtitle}
            centered
            light
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {servicesData.map((service) => {
              const IconComponent = Icons[service.icon] || Icons.Box;
              const serviceT = t.services.items[service.key];
              return (
                <Card
                  key={service.key}
                  className="group bg-slate-800/50 border-slate-700/50 hover:border-emerald-700 hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300 h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20 group-hover:shadow-emerald-500/30 transition-shadow duration-300">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{serviceT.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{serviceT.shortDesc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                variant="outline"
                className="border-slate-600 text-slate-300 hover:border-emerald-700 hover:text-white">
                {t.services.viewAll}
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* USPs Section */}
      <section className="py-24 bg-gradient-to-br from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            badge={t.usps.badge}
            title={t.usps.title}
            subtitle={t.usps.subtitle}
            centered
            light
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {uspsData.map((usp) => {
              const IconComponent = Icons[usp.icon] || Icons.Check;
              const uspT = t.usps.items[usp.key];
              return (
                <div
                  key={usp.key}
                  className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-emerald-500/30 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6 group-hover:bg-emerald-500/30 transition-colors duration-300">
                    <IconComponent className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{uspT.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{uspT.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Verticals Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <SectionHeading
              badge={t.verticals.badge}
              title={t.verticals.title}
              subtitle={t.verticals.subtitle}
              centered
            />

            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {t.verticals.items.map((vertical, index) => (
                <span
                  key={index}
                  className="px-4 py-2 rounded-full bg-slate-800 text-slate-300 text-sm font-medium hover:bg-emerald-900/20 hover:text-emerald-400 transition-colors duration-200 cursor-default">
                  {vertical}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 lg:p-12">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { icon: Shield, ...t.compliance.cards.gdpr },
                    { icon: CheckCircle, ...t.compliance.cards.doi },
                    { icon: Lock, ...t.compliance.cards.secure },
                    { icon: FileCheck, ...t.compliance.cards.documented },
                  ].map((item, i) => (
                    <div key={i} className="bg-slate-800 rounded-xl p-5 shadow-sm">
                      <item.icon className="w-8 h-8 text-emerald-400 mb-3" />
                      <div className="font-semibold text-white text-sm mb-1">{item.label}</div>
                      <div className="text-slate-400 text-xs">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <SectionHeading
                badge={t.compliance.badge}
                title={t.compliance.title}
                subtitle={t.compliance.subtitle}
              />
              <ul className="mt-8 space-y-4">
                {t.compliance.items.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-xl text-emerald-100 mb-10 max-w-2xl mx-auto">{t.cta.subtitle}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-xl shadow-emerald-900/30 h-14 px-8 text-base font-semibold">
                {t.cta.requestLeadFlow}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/advertisers">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 h-14 px-8 text-base">
                {t.cta.learnMore}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
